import {ConfigApi} from '@backstage/core-plugin-api';
import {ScmAuthApi} from '@backstage/integration-react';
import {PequiGithubActionsApi} from "./PequiGithubActionsApi.ts";
import {Octokit, RestEndpointMethodTypes} from "@octokit/rest";
import {readGithubIntegrationConfigs} from "@backstage/integration";

export class PequiGithubActionsClient implements PequiGithubActionsApi {

    private readonly configApi: ConfigApi;
    private readonly scmAuthApi: ScmAuthApi;

    constructor(options: { configApi: ConfigApi; scmAuthApi: ScmAuthApi }) {
        this.configApi = options.configApi;
        this.scmAuthApi = options.scmAuthApi;
    }


    private getOctokit = async (hostname: string = 'github.com'): Promise<Octokit> => {
        const {token} = await this.scmAuthApi.getCredentials({
            url: `https://${hostname}/`,
            additionalScope: {
                customScopes: {
                    github: ['repo'],
                },
            },
        });
        const configs = readGithubIntegrationConfigs(
            this.configApi.getOptionalConfigArray('integrations.github') ?? [],
        );
        const githubIntegrationConfig = configs.find(v => v.host === hostname);
        const baseUrl = githubIntegrationConfig?.apiBaseUrl;
        return new Octokit({auth: token, baseUrl});
    }

    listBranches = async (options: {
        hostname?: string;
        owner: string;
        repo: string;
        page?: number;
    }): Promise<
        RestEndpointMethodTypes['repos']['listBranches']['response']['data']
    > => {
        const {hostname, owner, repo, page = 0} = options;
        const octokit = await this.getOctokit(hostname);
        const response = await octokit.rest.repos.listBranches({
            owner,
            repo,
            per_page: 100,
            page,
        });
        return response.data;
    }

    getDefaultBranch = async (options: {
        hostname?: string;
        owner: string;
        repo: string;
    }): Promise<
        RestEndpointMethodTypes['repos']['get']['response']['data']['default_branch']
    > => {
        const {hostname, owner, repo} = options;
        const octokit = await this.getOctokit(hostname);
        const response = await octokit.rest.repos.get({
            owner,
            repo,
        });

        return response.data.default_branch;
    }

    listWorkflowRuns = async (options: {
        hostname?: string;
        owner: string;
        repo: string;
        pageSize?: number;
        page?: number;
        branch?: string;
    }): Promise<
        RestEndpointMethodTypes['actions']['listWorkflowRuns']['response']['data']
    > => {
        const {hostname, owner, repo, pageSize = 100, page = 0, branch} = options;

        const octokit = await this.getOctokit(hostname);
        const workflowRuns = await octokit.actions.listWorkflowRunsForRepo({
            owner,
            repo,
            per_page: pageSize,
            page,
            ...(branch ? {branch} : {}),
        });

        return workflowRuns.data;
    }

    createWorkflowDispatch = async (options: {
        hostname?: string;
        owner: string;
        repo: string;
        workflow_id: string;
        ref: string,
        inputs: Record<string, string>
    }): Promise<void> => {
        const {hostname, owner, repo, workflow_id, ref, inputs} = options;
        const octokit = await this.getOctokit(hostname);
        await octokit.actions.createWorkflowDispatch({
            owner,
            repo,
            workflow_id: workflow_id,
            ref: ref,
            inputs: inputs
        })
    }

    listJobsForWorkflowRun = async (options: {
        hostname?: string;
        owner: string;
        repo: string;
        id: number;
        pageSize?: number;
        page?: number;
    }): Promise<
        RestEndpointMethodTypes['actions']['listJobsForWorkflowRun']['response']['data']
    > => {
        const {hostname, owner, repo, id, pageSize = 100, page = 0} = options;

        const octokit = await this.getOctokit(hostname);
        const jobs = await octokit.actions.listJobsForWorkflowRun({
            owner,
            repo,
            run_id: id,
            per_page: pageSize,
            page,
        });

        return jobs.data;
    }

    getWorkflowRun = async (options: {
        hostname?: string;
        owner: string;
        repo: string;
        id: number;
    }): Promise<
        RestEndpointMethodTypes['actions']['getWorkflowRun']['response']['data']
    > => {
        const {hostname, owner, repo, id} = options;

        const octokit = await this.getOctokit(hostname);
        const run = await octokit.actions.getWorkflowRun({
            owner,
            repo,
            run_id: id,
        });

        return run.data;
    }


    downloadJobLogsForWorkflowRun = async (options: {
        hostname?: string;
        owner: string;
        repo: string;
        runId: number;
    }): Promise<
        RestEndpointMethodTypes['actions']['downloadJobLogsForWorkflowRun']['response']['data']
    > => {
        const {hostname, owner, repo, runId} = options;

        const octokit = await this.getOctokit(hostname);
        const workflow = await octokit.actions.downloadJobLogsForWorkflowRun({
            owner,
            repo,
            job_id: runId,
        });

        return workflow.data;
    }
}
